/**
 * Geo-scoped config revision — session refresh ONLY when admin changed the
 * exact scope that resolves this visitor's hero CTA or country template.
 *
 * Hero CTA cascade (Worker / cta-flags): city_ar > neighborhood_ar >
 * province_ar > country (ISO2) > country DEFAULT > edge fallback.
 *
 * We watch only the winning scope (heroCtaSource), not every geo field present
 * in the session — so a change in CABA does not refresh Mendoza.
 */

import {
  getServerTemplateConfig,
  type ServerTemplateConfig,
} from "@/lib/serverTemplateDetection";

export const GEO_CONFIG_REVISION_LS_KEY = "geo_config_revision_updated";
export const GEO_CONFIG_REVISION_SEEN_SS_KEY = "geo_config_revision_seen";
export const GEO_CONFIG_REVISION_EVENT = "geo-config-revision";

export interface GeoConfigRevisionPayload {
  revision_ms: number;
  scopes: Record<string, number>;
}

export type GeoRevisionKind =
  | "cta"
  | "neighborhood"
  | "country_template"
  | "mejores_amigos";

export const MEJORES_AMIGOS_GLOBAL_SCOPE = "mejores_amigos:global";

export interface GeoRevisionEventDetail {
  bumpedScopes: string[];
  kinds: GeoRevisionKind[];
}

/** Sources where geo DB overrides do not drive the hero CTA for this session. */
const NO_CTA_WATCH_SOURCES = new Set([
  "template_no_hero",
  "global_hide_hero_cta",
  "kill_switch",
  "metro_premium_override",
]);

export function normGeoKeyPart(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function scopeKeyNeighborhood(city: string, neighborhood: string): string {
  return `neighborhood_ar:${normGeoKeyPart(city)}:${normGeoKeyPart(neighborhood)}`;
}

function sessionCity(cfg: ServerTemplateConfig): string {
  return (
    cfg.city?.trim() ||
    cfg.neighborhoodCity?.trim() ||
    cfg.cfCity?.trim() ||
    ""
  );
}

/**
 * Scopes to watch for hero CTA admin changes — derived from the Worker-resolved
 * `heroCtaSource` (the scope that actually won the cascade for this session).
 */
export function buildCtaWatchScopes(
  cfg: ServerTemplateConfig | null | undefined,
): string[] {
  if (!cfg) return [];

  // site-settings heroCta override → geo wishlist changes don't affect this button
  if (cfg.heroCta?.enabled === true) return [];

  const source = (cfg.heroCtaSource || "").trim();
  if (!source || NO_CTA_WATCH_SOURCES.has(source)) return [];

  const country = (cfg.country || "").toUpperCase();
  const city = sessionCity(cfg);
  const province = cfg.province?.trim() || "";
  const nb = cfg.neighborhood?.trim() || "";
  const nbCity = city || cfg.neighborhoodCity?.trim() || "";

  switch (source) {
    case "neighborhood_ar":
      return nbCity && nb ? [scopeKeyNeighborhood(nbCity, nb)] : [];
    case "city_ar":
      return city ? [`city_ar:${normGeoKeyPart(city)}`] : [];
    case "province_ar":
    case "province_ar_none":
    case "province_ar_none_killswitch":
      return province ? [`province_ar:${province}`] : [];
    case "country_ar":
      return country === "AR" ? ["country:AR"] : [];
    case "country_db":
    case "country":
      return country && country !== "UNKNOWN" ? [`country:${country}`] : [];
    case "country_default":
      return ["country:DEFAULT"];
    case "edge_intl":
      // Sin entry DB aún; si admin crea country:XX, esta sesión debe enterarse
      return country && country !== "UNKNOWN" ? [`country:${country}`] : [];
    case "edge_fallback":
    case "edge_when_province_unknown":
    case "edge_config":
      if (country === "AR" && province) return [`province_ar:${province}`];
      if (country === "AR") return ["country:AR"];
      return [];
    default:
      // Fuentes desconocidas: nivel país (intl) o provincia (AR)
      if (country && country !== "UNKNOWN" && country !== "AR") {
        return [`country:${country}`];
      }
      if (country === "AR" && province) return [`province_ar:${province}`];
      if (country === "AR") return ["country:AR"];
      return [];
  }
}

/**
 * Country template overrides: watch ISO2 siempre; DEFAULT solo si la sesión
 * hereda el fallback global (sin override explícito en CountryTemplateAdmin).
 */
export function buildCountryTemplateWatchScopes(
  cfg: ServerTemplateConfig | null | undefined,
): string[] {
  if (!cfg) return [];
  const country = (cfg.country || "").toUpperCase();
  if (!country || country === "UNKNOWN") return [];
  const keys = [`country_template:${country}`];
  if (cfg.countryTemplateOverride !== true) {
    keys.push("country_template:DEFAULT");
  }
  return keys;
}

/**
 * Mejores Amigos tier URLs (global KV) — solo sesiones que ya muestran el CTA.
 */
export function buildMejoresAmigosWatchScopes(
  cfg: ServerTemplateConfig | null | undefined,
): string[] {
  if (!cfg?.showBestFriends) return [];
  return [MEJORES_AMIGOS_GLOBAL_SCOPE];
}

/** Union of CTA + template + mejores amigos scopes this session should react to. */
export function buildSessionScopeKeys(
  cfg: ServerTemplateConfig | null | undefined,
): string[] {
  return [
    ...new Set([
      ...buildCtaWatchScopes(cfg),
      ...buildCountryTemplateWatchScopes(cfg),
      ...buildMejoresAmigosWatchScopes(cfg),
    ]),
  ];
}

export function sessionShouldPollGeoRevision(
  cfg: ServerTemplateConfig | null | undefined,
): boolean {
  if (cfg?.adminSpaBypass) return false;
  return buildSessionScopeKeys(cfg).length > 0;
}

export function classifyRevisionScope(scope: string): GeoRevisionKind {
  if (scope.startsWith("mejores_amigos:")) return "mejores_amigos";
  if (scope.startsWith("country_template:")) return "country_template";
  if (scope.startsWith("neighborhood_ar:")) return "neighborhood";
  return "cta";
}

export function readSeenRevisions(): Record<string, number> {
  if (typeof sessionStorage === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(GEO_CONFIG_REVISION_SEEN_SS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export function writeSeenRevisions(scopes: Record<string, number>): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(GEO_CONFIG_REVISION_SEEN_SS_KEY, JSON.stringify(scopes));
  } catch {
    /* quota / private mode */
  }
}

export function findBumpedSessionScopes(
  sessionKeys: string[],
  serverScopes: Record<string, number>,
  lastSeen: Record<string, number>,
): string[] {
  const bumped: string[] = [];
  for (const key of sessionKeys) {
    const serverMs = serverScopes[key];
    if (typeof serverMs !== "number" || !Number.isFinite(serverMs)) continue;
    const seenMs = lastSeen[key];
    // Key NUNCA vista = primera visita (o scope nuevo tras refinar geo): NO es
    // un bump — este load ya trae la config fresca del Worker, no hay nada que
    // refrescar. Tratar undefined como 0 forzaba window.location.reload() a
    // TODO visitante nuevo (hasta 2-3 recargas encadenadas a medida que la
    // sesión sumaba scopes: país → provincia → ciudad). Visto en el replay
    // 019f6c0a… (mobile: doble navigation en el arranque) y reproducido live.
    // Limitación aceptada (review 2026-07-16): una revisión publicada por el
    // admin ENTRE el load de la página y el primer poll de la sesión se absorbe
    // como baseline (se registra como "seen" sin disparar refresh) — se pierde
    // UN refresh en esa ventana de segundos. Tradeoff consciente: la
    // alternativa (tratar unseen como stale) es exactamente el bug de arriba.
    if (seenMs === undefined) continue;
    if (serverMs > seenMs) bumped.push(key);
  }
  return bumped;
}

export function kindsFromScopes(scopes: string[]): GeoRevisionKind[] {
  const set = new Set<GeoRevisionKind>();
  for (const s of scopes) set.add(classifyRevisionScope(s));
  return [...set];
}

export function broadcastGeoConfigRevision(scopeKeys: string[]): void {
  if (typeof window === "undefined" || scopeKeys.length === 0) return;
  const detail: GeoRevisionEventDetail = {
    bumpedScopes: scopeKeys,
    kinds: kindsFromScopes(scopeKeys),
  };
  try {
    localStorage.setItem(GEO_CONFIG_REVISION_LS_KEY, JSON.stringify(detail));
  } catch {
    /* no-op */
  }
  const filtered = filterRevisionForSession(
    detail,
    buildSessionScopeKeys(getServerTemplateConfig()),
  );
  if (filtered) dispatchGeoRevisionEvent(filtered);
}

export async function fetchGeoConfigRevision(): Promise<GeoConfigRevisionPayload | null> {
  try {
    const res = await fetch("/api/geo-config-revision", {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as GeoConfigRevisionPayload;
    if (!data || typeof data !== "object") return null;
    return {
      revision_ms: typeof data.revision_ms === "number" ? data.revision_ms : 0,
      scopes:
        data.scopes && typeof data.scopes === "object" && !Array.isArray(data.scopes)
          ? data.scopes
          : {},
    };
  } catch {
    return null;
  }
}

export function applyRevisionSeen(
  serverScopes: Record<string, number>,
  keys: string[],
): void {
  const seen = readSeenRevisions();
  const now = Date.now();
  for (const key of keys) {
    const ms = serverScopes[key];
    seen[key] = typeof ms === "number" && Number.isFinite(ms) ? ms : now;
  }
  writeSeenRevisions(seen);
}

export function filterRevisionForSession(
  detail: GeoRevisionEventDetail,
  sessionKeys: string[],
): GeoRevisionEventDetail | null {
  if (sessionKeys.length === 0) return null;
  const keySet = new Set(sessionKeys);
  const bumped = detail.bumpedScopes.filter((k) => keySet.has(k));
  if (bumped.length === 0) return null;
  return { bumpedScopes: bumped, kinds: kindsFromScopes(bumped) };
}

export function notifyGeoRevisionFromResponse(body: unknown): void {
  if (!body || typeof body !== "object") return;
  const scopes = (body as { geo_revision_scopes?: unknown }).geo_revision_scopes;
  if (
    Array.isArray(scopes) &&
    scopes.length > 0 &&
    scopes.every((s) => typeof s === "string")
  ) {
    broadcastGeoConfigRevision(scopes);
  }
}

export function dispatchGeoRevisionEvent(detail: GeoRevisionEventDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<GeoRevisionEventDetail>(GEO_CONFIG_REVISION_EVENT, { detail }),
  );
}

/** For tests / admin debug: describe which scopes this session watches. */
export function describeSessionRevisionWatch(
  cfg: ServerTemplateConfig | null | undefined,
): {
  cta: string[];
  country_template: string[];
  mejores_amigos: string[];
  all: string[];
  heroCtaSource: string | null;
  polls: boolean;
} {
  const cta = buildCtaWatchScopes(cfg);
  const country_template = buildCountryTemplateWatchScopes(cfg);
  const mejores_amigos = buildMejoresAmigosWatchScopes(cfg);
  return {
    cta,
    country_template,
    mejores_amigos,
    all: [...new Set([...cta, ...country_template, ...mejores_amigos])],
    heroCtaSource: cfg?.heroCtaSource?.trim() || null,
    polls: cta.length + country_template.length + mejores_amigos.length > 0,
  };
}